/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2024
 */
import { ErrorResponseModel } from '../../../../../../core/models';
import { FIPApiService, Layer7ApiService } from '../../../http';
import { Injectable } from '@angular/core';
import { L7BackendServiceModel, TemplateModel } from '../../../models';
import { LogService, UtilsService } from '../../../../../../core/services';
import { WizardTitleEnum } from '../../../enums';
import { forkJoin, Observable } from 'rxjs';

@Injectable()
export class Layer47SaveService {
    constructor(
        private fipApiService: FIPApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private utilsService: UtilsService
    ) {
    }

    public propertiesToExclude(): string[] {
        return ['title', 'fipEnabled'];
    }

    /**
     * Validate save template.
     * @param saveTemplateObject
     * @param template
     */
    public validateSaveTemplate(saveTemplateObject: any, template: TemplateModel): Observable<any> {
        return new Observable(observer => {
            // See if there is a frontend or monitor, if so get the floating ip and backend details.
            if ((saveTemplateObject[WizardTitleEnum.FRONTENDS] && saveTemplateObject[WizardTitleEnum.FRONTENDS].length) ||
                (saveTemplateObject[WizardTitleEnum.MONITORS] && saveTemplateObject[WizardTitleEnum.MONITORS].length)) {
                this.getServiceDetails(saveTemplateObject)
                    .subscribe(({ fipReference, backendReferences }) => {
                        observer.next(this.formatTemplate(fipReference, backendReferences, template));
                        observer.complete();
                    });
            } else {
                observer.next(template);
                observer.complete();
            }
        });
    }

    /**
     * Helper function to get FIP and Backend details for template being saved.
     * @param saveTemplateObject
     * @private
     */
    private getServiceDetails(saveTemplateObject: any): Observable<any> {
        return new Observable(observer => {
            // Get latest floating ips as one could have been created in the wizard.
            const apiCalls = {
                floatingIps: this.fipApiService.getFloatingIPs(),
            };

            if (saveTemplateObject[WizardTitleEnum.MONITORS] && saveTemplateObject[WizardTitleEnum.MONITORS].length) {
                apiCalls['backends'] = this.layer7ApiService.getBackends();
            }

            forkJoin(apiCalls)
                .subscribe({
                    next: (dataGroup: any): void => {
                        let fipReference: string[], backendReferences: any[];
                        if (dataGroup['floatingIps']?.length) {
                            const fipServices = saveTemplateObject[WizardTitleEnum.FRONTENDS] ||
                                saveTemplateObject[WizardTitleEnum.MONITORS];
                            fipReference = this.getFipReference(fipServices, dataGroup['floatingIps']);
                        }

                        if (dataGroup['backends']?.length) {
                            backendReferences = this.getBackendReferences(saveTemplateObject, dataGroup['backends']);
                        }

                        observer.next({ fipReference, backendReferences });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get FIP reference
     * @param service - frontend or servicesMonitor
     * @param floatingIps
     * @private
     */
    private getFipReference(service: any[], floatingIps: any[]): any[] {
        const fipReference = [];

        for (let i = 0; i < service.length; i++) {
            const ipObject = floatingIps.find(ip => ip.id === service[i].fipId.value);
            if (ipObject && ipObject.ip) {
                fipReference.push(ipObject.ip);
                service[i]['fipId'] = {
                    value: '$' + (i + 1),
                    hidden: false
                };
            } else {
                this.logService.warn(
                    'convertControllerStateObjectForExport',
                    'No floating IP matches found'
                );
            }
        }

        return fipReference;
    }

    /**
     * Get backend references.
     * @param saveObj
     * @param backends
     * @private
     */
    private getBackendReferences(saveObj: any, backends: any[]): any {
        const acls: any[] = saveObj[WizardTitleEnum.ACLS];
        const backendReferences: any[] = [];
        if (acls.length) {
            for (const acl of acls) {
                const backendName = acl.redirectLocation?.value;
                if (!backendName) {
                    this.logService.warn('Layer47SaveService: getBackendReferences', 'Unable to match a backend.');
                } else {
                    backendReferences.push({
                        id: this.getIdByName(backends, backendName),
                        name: backendName
                    });
                }
            }
        }

        return backendReferences;
    }

    /**
     * Get the backend id by name.
     * @param backends
     * @param backendName
     * @private
     */
    private getIdByName(backends: any[], backendName: any): string {
        return backends.find((backend: L7BackendServiceModel): boolean => backend.name === backendName)?.id;
    }

    /**
     * Format the template ready for saving.
     * @param fipReference
     * @param backendReferences
     * @param template
     * @private
     */
    private formatTemplate(fipReference: any[], backendReferences: any[], template: TemplateModel): TemplateModel {
        if (backendReferences?.length) {
            this.utilsService.createArrayIfNotExist(template, 'backendReferences');
            template['backendReferences'] = backendReferences;
        }

        if (fipReference?.length) {
            this.utilsService.createArrayIfNotExist(template, 'fipsReference');
            template['fipsReference'] = fipReference;
        }

        return template;
    }
}
